import { mergeSortedArrays } from "@multimediallc/web-utils"
import {
    createEntityAdapter,
    createSelector,
    createSlice,
} from "@reduxjs/toolkit"
import { createApi } from "@reduxjs/toolkit/query/react"
import { ChatEntityType } from "../interfaces/chat"
import { compareChatTimestamp } from "../utils/chatUtil"
import { cbBaseQuery } from "../utils/myFetch"
import type { RootState } from "./store"
import type {
    ChatEntityData,
    ChatEntityID,
    LogMessageData,
    PmData,
    ReactRoomMessage,
    RoomMessageData,
    RoomNoticeData,
} from "../interfaces/chat"
import type {
    IPrivateMessage,
    IRoomNotice,
    LogMessageType,
} from "@multimediallc/web-utils/types"
import type { EntityState, PayloadAction } from "@reduxjs/toolkit"

const messagesAdapter = createEntityAdapter<RoomMessageData>({
    sortComparer: compareChatTimestamp,
})

const noticesAdapter = createEntityAdapter<RoomNoticeData>({
    sortComparer: compareChatTimestamp,
})

const logAdapter = createEntityAdapter<LogMessageData>({
    sortComparer: compareChatTimestamp,
})

const pmsAdapter = createEntityAdapter<PmData>({
    sortComparer: compareChatTimestamp,
})

export interface ChatState {
    messages: EntityState<RoomMessageData, ChatEntityID>
    notices: EntityState<RoomNoticeData, ChatEntityID>
    logs: EntityState<LogMessageData, ChatEntityID>

    // NOTE - This PMs state is really only for mobile broadcast chat. When PMs are brought into Messaging,
    //        we will likely want that to be the single source of PMs truth, and broadcast chat will just
    //        keep an array that keys into Messaging's normalized PM storage
    pms: EntityState<PmData, ChatEntityID>

    usernameToMessageEntityIDMap: { [username: string]: ChatEntityID[] }
    usernameToPmEntityIDMap: { [username: string]: ChatEntityID[] }
    latestTimestamp: number
    incingID: number
}

const initialState: ChatState = {
    messages: messagesAdapter.getInitialState(),
    notices: noticesAdapter.getInitialState(),
    logs: logAdapter.getInitialState(),
    pms: pmsAdapter.getInitialState(),
    usernameToMessageEntityIDMap: {},
    usernameToPmEntityIDMap: {},
    latestTimestamp: 0,
    incingID: 0,
}

export const chatSlice = createSlice({
    name: "chat",
    initialState: initialState,
    reducers: {
        newChatMessage: (
            state,
            { payload }: PayloadAction<ReactRoomMessage>,
        ) => {
            const derivedTimestamp = payload.ts ?? state.latestTimestamp
            const id = `message-${payload.messageID}`
            const newPayload: RoomMessageData = {
                ...payload,
                id,
                derivedTimestamp: derivedTimestamp,
                timeReceived: Date.now(),
                entityType: ChatEntityType.RoomMessage,
            }

            messagesAdapter.addOne(state.messages, newPayload)

            const username = payload.fromUser.username
            if (state.usernameToMessageEntityIDMap[username] === undefined) {
                state.usernameToMessageEntityIDMap[username] = []
            }
            state.usernameToMessageEntityIDMap[payload.fromUser.username].push(
                id,
            )

            state.latestTimestamp = Math.max(
                state.latestTimestamp,
                derivedTimestamp,
            )
        },
        newChatNotice: (state, { payload }: PayloadAction<IRoomNotice>) => {
            const derivedTimestamp = payload.ts ?? state.latestTimestamp
            const newPayload: RoomNoticeData = {
                ...payload,
                id: `notice-${state.incingID}`,
                derivedTimestamp: derivedTimestamp,
                timeReceived: Date.now(),
                entityType: ChatEntityType.RoomNotice,
            }

            noticesAdapter.addOne(state.notices, newPayload)
            state.incingID += 1
            state.latestTimestamp = Math.max(
                state.latestTimestamp,
                derivedTimestamp,
            )
        },
        newLog: (state, { payload }: PayloadAction<LogMessageType>) => {
            const derivedTimestamp = state.latestTimestamp
            const newPayload: LogMessageData = {
                message: payload,
                id: `log-${state.incingID}`,
                derivedTimestamp: derivedTimestamp,
                timeReceived: Date.now(),
                entityType: ChatEntityType.LogMessage,
            }

            logAdapter.addOne(state.logs, newPayload)
            state.incingID += 1
            state.latestTimestamp = Math.max(
                state.latestTimestamp,
                derivedTimestamp,
            )
        },
        newChatPm: (state, { payload }: PayloadAction<IPrivateMessage>) => {
            const derivedTimestamp =
                payload.ts ??
                payload.createdAt?.getTime() ??
                state.latestTimestamp
            const id = `pm-${payload.messageID}`
            const newPayload: PmData = {
                ...payload,
                id,
                derivedTimestamp: derivedTimestamp,
                timeReceived: Date.now(),
                entityType: ChatEntityType.PmMessage,
            }

            pmsAdapter.addOne(state.pms, newPayload)

            const username = payload.fromUser.username
            if (state.usernameToPmEntityIDMap[username] === undefined) {
                state.usernameToPmEntityIDMap[username] = []
            }
            state.usernameToPmEntityIDMap[username].push(id)

            state.latestTimestamp = Math.max(
                state.latestTimestamp,
                derivedTimestamp,
            )
        },
        userChatDeleted: (
            state,
            { payload: username }: PayloadAction<string>,
        ) => {
            messagesAdapter.removeMany(
                state.messages,
                state.usernameToMessageEntityIDMap[username] ?? [],
            )
            pmsAdapter.removeMany(
                state.pms,
                state.usernameToPmEntityIDMap[username] ?? [],
            )
        },
    },
})

interface MessageRenderOptionsResponse {
    show_emoticons: boolean
    ignored_emoticons: string[]
}

export const chatApi = createApi({
    reducerPath: "chatApi",
    baseQuery: cbBaseQuery({
        baseUrl: "/api/ts/chat/",
    }),
    endpoints: (builder) => ({
        messageRenderOptions: builder.query<MessageRenderOptionsResponse, void>(
            {
                query: () => ({
                    url: "message-render-options/",
                    method: "GET",
                }),
                keepUnusedDataFor: 60 * 60 * 24,
            },
        ),
    }),
})

type UpdateQueryDataResult = ReturnType<typeof chatApi.util.updateQueryData>

export const addIgnoredEmoticon = (
    emoticonName: string,
): UpdateQueryDataResult => {
    return chatApi.util.updateQueryData(
        "messageRenderOptions",
        undefined,
        (draft) => {
            if (draft.ignored_emoticons === undefined) {
                draft.ignored_emoticons = [emoticonName]
            } else {
                draft.ignored_emoticons.push(emoticonName)
            }
            return draft
        },
    )
}

export const removeIgnoredEmoticon = (
    emoticonName: string,
): UpdateQueryDataResult => {
    return chatApi.util.updateQueryData(
        "messageRenderOptions",
        undefined,
        (draft) => {
            const index = draft.ignored_emoticons.findIndex(
                (ignoredEmoticon) => ignoredEmoticon === emoticonName,
            )
            if (index >= 0) {
                draft.ignored_emoticons.splice(index, 1)
            }
            return draft
        },
    )
}

export const { useMessageRenderOptionsQuery } = chatApi

export const {
    newChatMessage,
    newChatNotice,
    newChatPm,
    newLog,
    userChatDeleted,
} = chatSlice.actions

export const {
    selectAll: selectAllChatMessages,
    selectById: selectChatMessageById,
} = messagesAdapter.getSelectors((state: RootState) => state.chat.messages)

export const {
    selectAll: selectAllChatNotices,
    selectById: selectChatNoticeById,
} = noticesAdapter.getSelectors((state: RootState) => state.chat.notices)

export const { selectAll: selectAllLogs, selectById: selectLogById } =
    logAdapter.getSelectors((state: RootState) => state.chat.logs)

export const { selectAll: selectAllChatPms, selectById: selectChatPmsById } =
    pmsAdapter.getSelectors((state: RootState) => state.chat.pms)

export const selectMobileBroadcastChat = createSelector(
    [
        selectAllChatMessages,
        selectAllChatNotices,
        selectAllLogs,
        selectAllChatPms,
    ],
    (messages, notices, logs, pms) => {
        return mergeSortedArrays(
            compareChatTimestamp,
            messages,
            notices,
            logs,
            pms,
        ) as ChatEntityData[]
    },
)

export default chatSlice.reducer
